/**
 * VnMessageComposeDialog — the broker→VN "Nachricht an Kunde" compose / reply
 * (Wave W5 broker→VN messaging; docs/design/2026-07-29_mandant-config-catalog.md → W5).
 *
 * Distinct from ComposeDialog (which drafts a mailbox e-mail, draft-only): this
 * sends a REAL free-text message to a VN via POST /communications/vn-messages.
 * The message is delivered on the VN's RESOLVED channel — a portal-VN gets a
 * portal-thread row + a timely notification; an email-only VN gets a
 * broker-branded email. The compose lets the broker:
 *   - pick/target the VN (customer subject) — searched live, OR pre-targeted when
 *     replying from a selected thread (`initialTarget`, changeable);
 *   - enter a Betreff + message body;
 *   - flag the message "Vertraulich" (→ the notification email is subject-only);
 *   - send, with HONEST states (idle → sending → sent | failed).
 * After a successful send it renders the sent message (subject + body + the
 * resolved delivery channel + a Vertraulich badge) — the outbound communication
 * row then surfaces in the Kommunikation Ausgang/Gesendet tab (parent invalidates).
 *
 * The dialog is presentational + stateful; the parent owns the network — it
 * injects `searchCustomers` (live customers.list / mock) and `onSend` (the
 * useSendVnMessage mutation). That keeps this component deterministically
 * testable with zero network.
 *
 * [Source: apps/web/src/features/communication-v2/components/ComposeDialog.tsx] (modal shell)
 * [Source: apps/web/src/features/communication-v2/components/MailReadingPane.tsx] (Kunde-search picker)
 * [Source: apps/api/src/modules/customer-portal/broker-message.service.ts] (delivery contract)
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  AlertTriangle,
  CheckCircle2,
  Lock,
  Mail,
  MessageSquarePlus,
  Search,
  Send,
  ShieldCheck,
  User,
  X,
} from 'lucide-react';
import { Button } from '../../../components/ui/Button';

/** A VN the broker can message (a customer subject). */
export interface VnTarget {
  subjectType: 'person' | 'firma';
  subjectId: string;
  name: string;
}

/** The exact payload sent to POST /communications/vn-messages. */
export interface VnMessagePayload {
  subjectType: 'person' | 'firma';
  subjectId: string;
  subject: string | null;
  body: string;
  confidential: boolean;
}

/** 201 result of the send — how the message was delivered. */
export interface VnSendResult {
  communication_id: string;
  delivery: 'portal' | 'email';
}

type SendState = 'idle' | 'sending' | 'sent' | 'failed';

interface VnMessageComposeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Pre-targeted VN (reply-from-thread). Changeable via "Ändern". */
  initialTarget?: VnTarget | null;
  /** Pre-filled Betreff (e.g. "Re: …" when replying). */
  initialSubject?: string;
  /** Live/mock VN search for the picker (parent injects the data source). */
  searchCustomers: (q: string) => Promise<VnTarget[]>;
  /** Send the message (parent wires useSendVnMessage). Resolves with the delivery result. */
  onSend: (payload: VnMessagePayload) => Promise<VnSendResult>;
  /** Fired once a message is sent (parent surfaces it in the Kommunikation view). */
  onSent?: (result: VnSendResult) => void;
}

export function VnMessageComposeDialog({
  open,
  onOpenChange,
  initialTarget = null,
  initialSubject = '',
  searchCustomers,
  onSend,
  onSent,
}: VnMessageComposeDialogProps) {
  const [target, setTarget] = useState<VnTarget | null>(initialTarget);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<VnTarget[]>([]);
  const [subject, setSubject] = useState(initialSubject);
  const [body, setBody] = useState('');
  const [confidential, setConfidential] = useState(false);
  const [state, setState] = useState<SendState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [result, setResult] = useState<VnSendResult | null>(null);
  // The values that were actually sent (rendered in the success panel).
  const sentRef = useRef<VnMessagePayload | null>(null);

  // Seed / reset on open (keyed on open + the initial props so a fresh open is clean).
  useEffect(() => {
    if (open) {
      setTarget(initialTarget);
      setPickerOpen(false);
      setQuery('');
      setResults([]);
      setSubject(initialSubject);
      setBody('');
      setConfidential(false);
      setState('idle');
      setErrorMsg(null);
      setResult(null);
      sentRef.current = null;
    }
    // Effect is keyed on open + the seed props only (mirrors ComposeDialog).
  }, [open, initialTarget, initialSubject]);

  // Debounced VN search when the picker is open.
  useEffect(() => {
    if (!pickerOpen) return;
    const q = query.trim();
    let cancelled = false;
    const t = setTimeout(() => {
      void searchCustomers(q).then((rows) => {
        if (!cancelled) setResults(rows);
      });
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [pickerOpen, query, searchCustomers]);

  // Close on Esc (unless mid-send).
  useEffect(() => {
    if (!open) return;
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape' && state !== 'sending') onOpenChange(false);
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange, state]);

  const canSend = target !== null && body.trim().length > 0 && state !== 'sending';

  async function handleSend() {
    if (target === null || body.trim().length === 0) return;
    const payload: VnMessagePayload = {
      subjectType: target.subjectType,
      subjectId: target.subjectId,
      subject: subject.trim() === '' ? null : subject.trim(),
      body,
      confidential,
    };
    setState('sending');
    setErrorMsg(null);
    try {
      const res = await onSend(payload);
      sentRef.current = payload;
      setResult(res);
      setState('sent');
      onSent?.(res);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : 'Senden fehlgeschlagen.');
      setState('failed');
    }
  }

  const sentTargetName = useMemo(() => target?.name ?? '', [target]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 p-4 backdrop-blur-sm">
      <div className="w-full max-w-lg overflow-hidden rounded-xl border bg-card shadow-lg">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 border-b p-4">
          <div className="flex flex-col gap-0.5 pr-4">
            <h2 className="flex items-center gap-2 font-semibold text-foreground">
              <MessageSquarePlus className="size-4 text-primary" />
              Nachricht an Kunde
            </h2>
            <p className="text-xs text-muted-foreground">
              Wird über den hinterlegten Kanal zugestellt: Kundenportal mit Benachrichtigung
              oder als E-Mail in Ihrem Branding.
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={() => onOpenChange(false)}
            aria-label="Schliessen"
            className="size-7 shrink-0"
          >
            <X className="size-4" />
          </Button>
        </div>

        {state === 'sent' && result ? (
          /* ── Sent confirmation — renders the message that was sent ── */
          <div className="flex flex-col gap-3 p-4">
            <div className="flex items-center gap-2 rounded-md border border-sem-ok/40 bg-sem-ok-bg px-3 py-2 text-sm text-sem-ok">
              <CheckCircle2 className="size-4 shrink-0" />
              {result.delivery === 'portal' ? (
                <span>Nachricht im Kundenportal zugestellt und benachrichtigt.</span>
              ) : (
                <span>Nachricht als E-Mail in Ihrem Branding gesendet.</span>
              )}
            </div>
            <div className="flex flex-col gap-1.5 rounded-md border border-border bg-background p-3">
              <div className="flex items-center gap-2 text-xs text-muted-foreground">
                {result.delivery === 'portal' ? (
                  <ShieldCheck className="size-3.5" />
                ) : (
                  <Mail className="size-3.5" />
                )}
                <span>An: {sentTargetName}</span>
                {sentRef.current?.confidential && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-secondary px-1.5 py-0.5 text-[0.65rem] font-medium text-foreground">
                    <Lock className="size-3" /> Vertraulich
                  </span>
                )}
              </div>
              {sentRef.current?.subject && (
                <p className="text-sm font-medium text-foreground">{sentRef.current.subject}</p>
              )}
              <p className="whitespace-pre-line text-sm text-muted-foreground">
                {sentRef.current?.body}
              </p>
            </div>
            <div className="flex items-center justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)}>
                Schliessen
              </Button>
              <Button variant="outline" onClick={() => setState('idle')}>
                <MessageSquarePlus className="size-4" /> Weitere Nachricht
              </Button>
            </div>
          </div>
        ) : (
          /* ── Compose form ── */
          <>
            <form
              id="vn-message-form"
              className="flex max-h-[60vh] flex-col gap-3 overflow-y-auto p-4"
              onSubmit={(e) => {
                e.preventDefault();
                void handleSend();
              }}
            >
              {/* Empfänger (VN) */}
              <div className="flex flex-col gap-1.5">
                <span className="text-xs text-muted-foreground">Empfänger (Kunde)</span>
                {target && !pickerOpen ? (
                  <div className="flex items-center justify-between gap-2 rounded-md border border-border bg-background px-3 py-2">
                    <span className="flex min-w-0 items-center gap-2 text-sm text-foreground">
                      <User className="size-3.5 shrink-0 text-muted-foreground" />
                      <span className="truncate">{target.name}</span>
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setPickerOpen(true);
                        setQuery('');
                      }}
                      className="shrink-0 text-xs font-medium text-primary underline-offset-4 hover:underline"
                    >
                      Ändern
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col gap-1.5">
                    <div className="relative">
                      <Search className="pointer-events-none absolute left-2.5 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
                      <input
                        autoFocus
                        type="text"
                        value={query}
                        onChange={(e) => {
                          setQuery(e.target.value);
                          if (!pickerOpen) setPickerOpen(true);
                        }}
                        onFocus={() => setPickerOpen(true)}
                        placeholder="Kunde suchen …"
                        aria-label="Kunde suchen"
                        className="h-9 w-full rounded-md border border-border bg-background pl-8 pr-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                      />
                    </div>
                    {pickerOpen && (
                      <ul className="max-h-40 overflow-y-auto rounded-md border bg-popover">
                        {results.length > 0 ? (
                          results.map((c) => (
                            <li key={`${c.subjectType}:${c.subjectId}`}>
                              <button
                                type="button"
                                onClick={() => {
                                  setTarget(c);
                                  setPickerOpen(false);
                                  setQuery('');
                                }}
                                className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-sm text-foreground transition-colors hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                              >
                                <User className="size-3.5 shrink-0 text-muted-foreground" />
                                {c.name}
                              </button>
                            </li>
                          ))
                        ) : (
                          <li className="px-2.5 py-2 text-sm text-muted-foreground">
                            Kein Kunde gefunden.
                          </li>
                        )}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Betreff */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="vn-message-subject" className="text-xs text-muted-foreground">
                  Betreff
                </label>
                <input
                  id="vn-message-subject"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder="Betreff (optional)"
                  className="h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              {/* Nachricht */}
              <div className="flex flex-col gap-1.5">
                <label htmlFor="vn-message-body" className="text-xs text-muted-foreground">
                  Nachricht
                </label>
                <textarea
                  id="vn-message-body"
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  placeholder="Ihre Nachricht an den Kunden …"
                  className="min-h-40 resize-none whitespace-pre-line rounded-md border border-border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                />
              </div>

              {/* Vertraulich toggle */}
              <label
                htmlFor="vn-message-confidential"
                className="flex cursor-pointer items-start gap-2 rounded-md border border-border bg-background p-2.5"
              >
                <input
                  id="vn-message-confidential"
                  type="checkbox"
                  checked={confidential}
                  onChange={(e) => setConfidential(e.target.checked)}
                  className="mt-0.5 size-4 shrink-0 accent-[hsl(var(--primary))]"
                />
                <span className="flex flex-col gap-0.5">
                  <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
                    <Lock className="size-3.5" /> Vertraulich
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Die Benachrichtigungs-E-Mail zeigt nur den Betreff, der Inhalt bleibt hinter
                    dem Portal-Login (revDSG / Berufsgeheimnis).
                  </span>
                </span>
              </label>

              {state === 'failed' && errorMsg && (
                <div
                  role="alert"
                  className="flex items-center gap-2 rounded-md border border-sem-alert/40 bg-sem-alert-bg px-3 py-2 text-sm text-sem-alert"
                >
                  <AlertTriangle className="size-4 shrink-0" />
                  {errorMsg}
                </div>
              )}
            </form>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 border-t p-4">
              <span className="text-xs text-muted-foreground">
                {target === null
                  ? 'Empfänger wählen, um zu senden.'
                  : 'Wird über den hinterlegten Kanal zugestellt.'}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  onClick={() => onOpenChange(false)}
                  disabled={state === 'sending'}
                >
                  Abbrechen
                </Button>
                <Button type="submit" form="vn-message-form" disabled={!canSend}>
                  <Send className="size-4" />
                  {state === 'sending' ? 'Senden …' : 'Senden'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
